import React from 'react';
import { Link } from 'react-router-dom';
import {
  colors,
  typography,
  borderRadius,
  getCosmicBackground,
  getCardStyle,
  getButtonStyle,
  getGradientTextStyle,
} from '../theme';

const LandingPage: React.FC = () => {
  return (
    <div style={{
      ...getCosmicBackground(),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Star pattern background */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `
          radial-gradient(2px 2px at 20px 30px, rgba(212, 175, 55, 0.4), transparent),
          radial-gradient(2px 2px at 40px 70px, rgba(157, 143, 219, 0.3), transparent),
          radial-gradient(1px 1px at 90px 40px, rgba(255, 255, 255, 0.5), transparent),
          radial-gradient(2px 2px at 130px 80px, rgba(212, 175, 55, 0.3), transparent),
          radial-gradient(1px 1px at 160px 120px, rgba(255, 255, 255, 0.4), transparent),
          radial-gradient(1.5px 1.5px at 200px 150px, rgba(157, 143, 219, 0.4), transparent),
          radial-gradient(1px 1px at 250px 100px, rgba(212, 175, 55, 0.5), transparent)
        `,
        backgroundSize: '300px 300px',
        animation: 'twinkle 4s ease-in-out infinite',
        pointerEvents: 'none',
      }} />

      {/* Radial glow effect */}
      <div style={{
        position: 'absolute',
        top: '-20%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '800px',
        height: '800px',
        background: 'radial-gradient(circle, rgba(107, 91, 189, 0.15) 0%, transparent 60%)',
        pointerEvents: 'none',
      }} />

      <div style={{
        ...getCardStyle('default'),
        textAlign: 'center',
        maxWidth: '700px',
        width: '100%',
        position: 'relative',
        zIndex: 1,
      }}>
        {/* Gold Star Logo */}
        <div style={{
          fontSize: '70px',
          marginBottom: '20px',
          filter: 'drop-shadow(0 0 20px rgba(212, 175, 55, 0.5))',
        }}>
          <span style={{
            display: 'inline-block',
            animation: 'float 6s ease-in-out infinite',
            color: colors.accent.gold,
            textShadow: `0 0 30px ${colors.accent.gold}`,
          }}>
{'\u2605'}
          </span>
        </div>

        {/* Main Title */}
        <h1 style={{
          fontFamily: typography.fontFamily.heading,
          fontSize: 'clamp(36px, 8vw, 56px)',
          fontWeight: typography.fontWeight.bold,
          letterSpacing: typography.letterSpacing.wide,
          margin: '0 0 16px 0',
          ...getGradientTextStyle('gold'),
          textShadow: '0 2px 20px rgba(212, 175, 55, 0.3)',
        }}>
          StarDecision
        </h1>

        {/* Subtitle */}
        <p style={{
          fontFamily: typography.fontFamily.heading,
          fontSize: 'clamp(18px, 4vw, 24px)',
          color: colors.primary.lavender,
          marginBottom: '32px',
          fontWeight: typography.fontWeight.normal,
          letterSpacing: typography.letterSpacing.wide,
        }}>
          Perfect Timing for Life's Big Decisions
        </p>

        {/* Description */}
        <p style={{
          fontSize: typography.fontSize.lg,
          color: colors.text.secondary,
          marginBottom: '40px',
          lineHeight: '1.8',
          maxWidth: '520px',
          margin: '0 auto 40px',
        }}>
          Discover optimal timing for your most important life decisions using advanced
          astrology calculations and crystal guidance. Make choices when the stars align
          in your favor.
        </p>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '20px',
          justifyContent: 'center',
          flexWrap: 'wrap',
          marginBottom: '50px',
        }}>
          <Link to="/register" style={{ textDecoration: 'none' }}>
            <button style={{
              ...getButtonStyle('gold'),
              fontSize: typography.fontSize.lg,
              padding: '16px 36px',
            }}>
{'\u2605'} Get Started
            </button>
          </Link>
          <Link to="/about" style={{ textDecoration: 'none' }}>
            <button style={{
              ...getButtonStyle('outline'),
              fontSize: typography.fontSize.lg,
              padding: '16px 36px',
            }}>
              Learn More
            </button>
          </Link>
        </div>

        {/* Features Preview */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: '24px',
          marginTop: '20px',
          paddingTop: '30px',
          borderTop: `1px solid ${colors.border.subtle}`,
        }}>
          {[
            {
              icon: '\u2606',
              title: 'Astrological Analysis',
              description: 'Precise planetary calculations',
            },
            {
              icon: '\u2666',
              title: 'Crystal Guidance',
              description: 'Personalized recommendations',
            },
            {
              icon: '\u25B3',
              title: 'Decision Categories',
              description: 'Life, career, relationships',
            },
          ].map((feature, index) => (
            <div
              key={index}
              style={{
                textAlign: 'center',
                padding: '20px 15px',
                borderRadius: borderRadius.lg,
                background: 'rgba(74, 63, 138, 0.15)',
                border: `1px solid ${colors.border.subtle}`,
                transition: 'all 0.3s ease',
              }}
            >
              <div style={{
                fontSize: '36px',
                marginBottom: '12px',
                color: colors.accent.gold,
                textShadow: `0 0 15px rgba(212, 175, 55, 0.4)`,
              }}>{feature.icon}</div>
              <h3 style={{
                fontFamily: typography.fontFamily.heading,
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.semibold,
                color: colors.text.starWhite,
                margin: '0 0 8px 0',
                letterSpacing: typography.letterSpacing.wide,
              }}>
                {feature.title}
              </h3>
              <p style={{
                fontSize: typography.fontSize.xs,
                color: colors.text.muted,
                margin: 0,
              }}>
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Sign In Link */}
        <div style={{
          marginTop: '40px',
          paddingTop: '24px',
          borderTop: `1px solid ${colors.border.subtle}`,
        }}>
          <p style={{
            fontSize: typography.fontSize.sm,
            color: colors.text.muted,
            margin: 0,
          }}>
            Already have an account?{' '}
            <Link to="/login" style={{
              color: colors.accent.gold,
              textDecoration: 'none',
              fontWeight: typography.fontWeight.medium,
              transition: 'all 0.3s ease',
            }}>
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
